import { useRef, useState } from "react";
import { WALL_STYLE, fmtDate } from "../../lib/art";
import { Framed } from "./Framed";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

const autoPos = (i) => ({ gx: 6 + (i % 4) * 23, gy: 8 + Math.floor(i / 4) * 30 });

let topZ = 10;

const Piece = ({ w, i, editable, wallRef, onMove, onOpen }) => {
  const [pos, setPos] = useState(w.gx != null ? { gx: w.gx, gy: w.gy } : autoPos(i));
  const [z, setZ] = useState(w.gx != null ? 2 : 1);
  const drag = useRef(null);
  const down = (e) => {
    if (!editable) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setZ(++topZ);
    drag.current = { x: e.clientX, y: e.clientY, start: pos, moved: false };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const r = wallRef.current.getBoundingClientRect();
    const dx = ((e.clientX - d.x) / r.width) * 100, dy = ((e.clientY - d.y) / r.height) * 100;
    if (Math.abs(dx) + Math.abs(dy) > 0.8) d.moved = true;
    setPos({ gx: Math.min(82, Math.max(0, d.start.gx + dx)), gy: Math.min(78, Math.max(0, d.start.gy + dy)) });
  };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.moved) return onOpen(w);
    onMove(w, pos);
  };
  return (
    <div data-testid={`gallery-piece-${w.work_id}`} className={`absolute w-[18%] min-w-[110px] ${editable ? "cursor-grab active:cursor-grabbing touch-none" : "cursor-zoom-in"}`}
      style={{ left: `${pos.gx}%`, top: `${pos.gy}%`, zIndex: z }} onPointerDown={down} onPointerMove={move} onPointerUp={up} onClick={editable ? undefined : () => onOpen(w)}>
      <Framed src={w.image_url} frame={w.frame === "none" ? "wood" : w.frame} />
      <div className="mt-1 mx-auto w-fit max-w-full px-2 py-0.5 rounded bg-white/90 text-xs sm:text-sm font-bold text-stone-700 truncate shadow">{w.title}</div>
    </div>
  );
};

export const GalleryWall = ({ works, wall = "cream", editable, onMove, footer }) => {
  const wallRef = useRef(null);
  const [open, setOpen] = useState(null);
  const rows = Math.max(1, Math.ceil(works.length / 4));
  return (
    <>
      <div ref={wallRef} data-testid="gallery-wall" className="relative w-full rounded-[2rem] overflow-hidden border-8 border-[#6b4a2b] shadow-2xl"
        style={{ ...WALL_STYLE[wall], minHeight: `max(520px, ${rows * 30 + 10}vw)`, aspectRatio: rows <= 2 ? "16/9" : undefined }}>
        <div className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />
        {works.map((w, i) => <Piece key={w.work_id} w={w} i={i} editable={editable} wallRef={wallRef} onMove={onMove} onOpen={setOpen} />)}
        <div className="absolute inset-x-0 bottom-0 h-8 bg-[#8b5e34] border-t-4 border-[#6b4a2b]" />
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="rounded-[2rem] max-w-2xl bg-[#FFFBF3]" data-testid="gallery-zoom-dialog">
          {open && (<>
            <DialogTitle className="text-3xl font-black text-[#B4471F]" data-testid="gallery-zoom-title">{open.title}</DialogTitle>
            <Framed src={open.image_url} frame={open.frame} className="max-w-[70vh] mx-auto w-full" />
            <div className="text-stone-500" data-testid="gallery-zoom-date">{fmtDate(open.created_at)}</div>
            {open.says && <div className="text-lg text-stone-700">「{open.says}」</div>}
            {footer?.(open)}
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
};
